import { format } from "date-fns";
import { CalendarIcon, Clock } from "lucide-react";
import { Button } from "../../Button";
import { Popover, PopoverTrigger, PopoverContent } from "../../Popover";
import CustomDatePicker from "../../CutomDatePicker";
import {
    Select,
    SelectTrigger,
    SelectContent,
    SelectValue,
    SelectItem,
} from "../../Select";
import { Label } from "../../Label";
import { RadioGroup, RadioGroupItem } from "../../RadioGroup";
import { cn } from "../../../../utils/classMerger";
import { BasicInfoStepProps, VideoPostType } from "../types";
import { isListingShownInPicker, isNicheVideoListing, listingSelectValue, normalizeListingType, showsAiEnhanceToggle, withNicheVideoEntry } from "../adhocListingTypes";
import { presetBlockedByPause, presetPick, rowsForPreset } from "../../../add-post/preset";
import {
    CONTENT_AUDIENCES,
    ContentAudience,
} from "../../../../types/contentAudience";
import {
    contentAudienceHint,
    leadGenSubtypeLabel,
} from "../../../../utils/contentAudience";
import { ListingType } from "../../../../types/userListing";
import { Textarea } from "../../Textarea";
import Checkbox from "../../Checkbox";
import React, { useEffect, useMemo, useState, useRef } from "react";
import CustomSelect, { OptionType } from "../../CustomSelect";
import { schoolDistrictOptionLabels } from "./school-district-options";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { toast } from "react-toastify";
import CustomToast from "../../CustomToast";
import { getAuthHeader } from "../../../../utils/authHeader";
import axios from "axios";
import LoadingScreen from "../../../../screens/LoadingScreen";

const levenshtein = (a: string, b: string): number => {
    const matrix: number[][] = [];
    const [la, lb] = [a.length, b.length];
    for (let i = 0; i <= la; i++) {
        matrix[i] = [i];
    }
    for (let j = 1; j <= lb; j++) {
        matrix[0][j] = j;
    }
    for (let i = 1; i <= la; i++) {
        for (let j = 1; j <= lb; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            matrix[i][j] = Math.min(
                matrix[i - 1][j] + 1, // deletion
                matrix[i][j - 1] + 1, // insertion
                matrix[i - 1][j - 1] + cost // substitution
            );
        }
    }
    return matrix[la][lb];
};

const similarity = (a: string, b: string): number => {
    const dist = levenshtein(a.toLowerCase(), b.toLowerCase());
    return 1 - dist / Math.max(a.length, b.length);
};

// Types for prompt tokens
interface PromptPart {
    type: 'text' | 'token';
    value: string;
    key?: string; // token key like "City"
}

// Parse prompt string to extract tokens
const parsePromptToTokens = (prompt: string): PromptPart[] => {
    const parts: PromptPart[] = [];
    const regex = /\[([^\]]+)\]/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(prompt)) !== null) {
        // Add text before the token
        if (match.index > lastIndex) {
            parts.push({
                type: 'text',
                value: prompt.substring(lastIndex, match.index)
            });
        }
        // Add the token
        parts.push({
            type: 'token',
            value: '',
            key: match[1]
        });
        lastIndex = regex.lastIndex;
    }
    // Add remaining text
    if (lastIndex < prompt.length) {
        parts.push({
            type: 'text',
            value: prompt.substring(lastIndex)
        });
    }
    return parts;
};

// Build final prompt from parts
const buildPromptFromParts = (parts: PromptPart[]): string => {
    return parts.map(part => {
        if (part.type === 'text') {
            return part.value;
        } else {
            return part.value || `[${part.key}]`;
        }
    }).join('');
};

const BasicInfoStep = ({
    openHouseDate,
    setOpenHouseDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    startPeriod,
    setStartPeriod,
    endPeriod,
    setEndPeriod,
    generalInstructions,
    setGeneralInstructions,
    aiEnhance,
    setAiEnhance,
    videoPostType,
    setVideoPostType,
    isVideoPost,
    schoolDistrict,
    setSchoolDistrict,
    tags,
    setTags,
    listing,
    isOpenHousePost,
    isAdHocPost,
    listings,
    setListings,
    selectedListing,
    setSelectedListing,
    confirmAiEnhanceDisable,
    onTokensValidChange,
    leadGenerationType: propLeadGenerationType,
    setLeadGenerationType: propSetLeadGenerationType,
    leadGenerationSubtypes: propLeadGenerationSubtypes,
    setLeadGenerationSubtypes: propSetLeadGenerationSubtypes,
    leadGenerationPrompts: propLeadGenerationPrompts,
    setLeadGenerationPrompts: propSetLeadGenerationPrompts,
    selectedPrompt: propSelectedPrompt,
    setSelectedPrompt: propSetSelectedPrompt,
    promptParts: propPromptParts,
    setPromptParts: propSetPromptParts,
    setAreTokensFilled,
    hideContent = false,
    hideAiEnhance = false,
    postCaptionedVideo: propPostCaptionedVideo,
    setPostCaptionedVideo: propSetPostCaptionedVideo,
    reviewCaptions,
    setReviewCaptions,
    contentAudience: propContentAudience,
    setContentAudience: propSetContentAudience,
    nicheEligible = false,
    preset,
    nicheResolved = true,
}: BasicInfoStepProps) => {
    const isMobile = useIsMobile(900);

    const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);
    const minuteOptions = Array.from({ length: 60 }, (_, i) => i);

    const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingSubtypes, setLoadingSubtypes] = useState<boolean>(false);
    const [loadingPrompts, setLoadingPrompts] = useState<boolean>(false);

    // Use parent-controlled state when provided; otherwise fall back to local state.
    const [localPostCaptionedVideo, setLocalPostCaptionedVideo] = useState<boolean>(false);
    const postCaptionedVideo = propPostCaptionedVideo !== undefined ? propPostCaptionedVideo : localPostCaptionedVideo;
    const setPostCaptionedVideo = propSetPostCaptionedVideo || setLocalPostCaptionedVideo;

    // Use props if available, otherwise fall back to local state for backward compatibility
    const [localLeadGenerationType, localSetLeadGenerationType] = useState<string>("");
    const [localLeadGenerationSubtypes, localSetLeadGenerationSubtypes] = useState<string[]>([]);
    const [localLeadGenerationPrompts, localSetLeadGenerationPrompts] = useState<string[]>([]);
    const [localSelectedPrompt, localSetSelectedPrompt] = useState<string>("");
    const [localPromptParts, localSetPromptParts] = useState<PromptPart[]>([]);

    const leadGenerationType = propLeadGenerationType !== undefined ? propLeadGenerationType : localLeadGenerationType;
    const setLeadGenerationType = propSetLeadGenerationType || localSetLeadGenerationType;
    const leadGenerationSubtypes = propLeadGenerationSubtypes !== undefined ? propLeadGenerationSubtypes : localLeadGenerationSubtypes;
    const setLeadGenerationSubtypes = propSetLeadGenerationSubtypes || localSetLeadGenerationSubtypes;
    const leadGenerationPrompts = propLeadGenerationPrompts !== undefined ? propLeadGenerationPrompts : localLeadGenerationPrompts;
    const setLeadGenerationPrompts = propSetLeadGenerationPrompts || localSetLeadGenerationPrompts;
    const selectedPrompt = propSelectedPrompt !== undefined ? propSelectedPrompt : localSelectedPrompt;
    const setSelectedPrompt = propSetSelectedPrompt || localSetSelectedPrompt;
    const promptParts = propPromptParts !== undefined ? propPromptParts : localPromptParts;
    const setPromptParts = propSetPromptParts || localSetPromptParts;
    // Voice family for this piece. Parent-controlled when the wizard
    // supplies it (that copy is the one persisted between sessions); the local
    // fallback keeps this component usable on its own.
    const [localContentAudience, localSetContentAudience] = useState<ContentAudience>(
        ContentAudience.Residential
    );
    const contentAudience = propContentAudience !== undefined ? propContentAudience : localContentAudience;
    const setContentAudience = propSetContentAudience || localSetContentAudience;

    const normalizedListingType = selectedListing?.listingType.toLowerCase().replace(/\s/g, "") ?? "";
    const isLeadGenListing = normalizedListingType === "leadgeneration";
    const isMyBrandListing = normalizedListingType === "mybrand";
    const isNicheVideoSelected = isNicheVideoListing(selectedListing?.listingType);
    // The server list plus the client-side My Niche Video clone (eligible agents only).
    const pickerListings = useMemo(
        () => withNicheVideoEntry(listings ?? [], nicheEligible),
        [listings, nicheEligible],
    );
    // Add Post page card: the dropdown's rows (empty = hidden, the card picks for the user).
    const presetRows = useMemo(
        () => (preset ? rowsForPreset(pickerListings, preset) : []),
        [pickerListings, preset],
    );
    const myBrandRow = pickerListings.find((r) => normalizeListingType(r.listingType) === "mybrand");
    const textOnlyRow = pickerListings.find((r) => r.listingType === ListingType.TextOnly);
    const isTextOnlySelected = selectedListing?.listingType === ListingType.TextOnly;
    // The pick a card makes for the user is missing from the account (no My Brand / Lead Generation row,
    // not eligible for My Niche Video once that is known), or the Listing / Captioned card has no property
    // campaign to offer.
    const presetPickMissing =
        !!preset &&
        presetRows.length === 0 &&
        !!listings &&
        listings.length > 0 &&
        (preset.card !== "niche" || nicheResolved) &&
        !presetPick(listings, preset, nicheEligible);
    const presetPaused = !!preset && !!listings && presetBlockedByPause(listings, preset, nicheEligible);
    const presetMissingText = presetPaused
        ? preset?.card === "niche"
            ? "Your My Brand campaign is paused. Resume it to post here."
            : "Your campaigns are paused. Resume one to post here."
        : preset?.card === "listing" || preset?.card === "captioned"
            ? "Add a listing first."
            : "This kind of post is not available on your account.";

    // Add a local state to track if no valid prompts are available
    const [noValidPrompts, setNoValidPrompts] = useState<boolean>(false);
    // Cache for storing prompts by subtype to avoid redundant API calls.
    // Keyed by audience as well as subtype — the same subtype yields a
    // different set of options per voice.
    const [promptsCache, setPromptsCache] = useState<Record<string, string[]>>({});
    // Token management
    const tokenInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
    const CHARACTER_LIMIT = 200;

    const promptCharCount = React.useMemo(
    () => buildPromptFromParts(promptParts).length,
    [promptParts]
);

    // Function to extract existing token values from current prompt parts
    const extractExistingTokenValues = (parts: PromptPart[]): Record<string, string> => {
        const existingValues: Record<string, string> = {};
        parts.filter(part => part.type === 'token' && part.key).forEach(part => {
            if (part.key && part.value) {
                existingValues[part.key] = part.value;
            }
        });
        return existingValues;
    };

    // Function to apply existing token values to newly parsed parts
    const applyExistingTokenValues = (newParts: PromptPart[], existingValues: Record<string, string>): PromptPart[] => {
        return newParts.map(part => {
            if (part.type === 'token' && part.key && existingValues[part.key]) {
                return { ...part, value: existingValues[part.key] };
            }
            return part;
        });
    };

    // Parse selected prompt into parts and sync to generalInstructions
    useEffect(() => {
        if (selectedPrompt) {
            // Extract existing token values before parsing new prompt
            const existingTokenValues = extractExistingTokenValues(promptParts);
            
            const parts = parsePromptToTokens(selectedPrompt);
            
            // Apply existing token values to preserve user input
            const partsWithExistingValues = applyExistingTokenValues(parts, existingTokenValues);
            
            setPromptParts(partsWithExistingValues);
            
            // Only initialize general instructions if no existing content or if this is the first time
            // This prevents overwriting user's filled values when navigating back to step 1
            if (!generalInstructions || promptParts.length === 0) {
                const initialText = buildPromptFromParts(partsWithExistingValues);
                setGeneralInstructions(initialText);
            }
        } else {
            setPromptParts([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPrompt, setGeneralInstructions, setPromptParts]);

    // Update generalInstructions whenever token values change
    useEffect(() => {
        if (promptParts.length > 0) {
            const finalText = buildPromptFromParts(promptParts);
            setGeneralInstructions(finalText);
        }
    }, [promptParts, setGeneralInstructions]);

    // Check if all tokens are filled
    const allTokensFilled = useMemo(() => {
        if (promptParts.length === 0) return true;
        const tokens = promptParts.filter(part => part.type === 'token');
        if (tokens.length === 0) return true;
        return tokens.every(token => token.value && token.value.trim().length > 0);
    }, [promptParts]);

    // Notify parent component when token validation changes
    useEffect(() => {
        if (onTokensValidChange) {
            onTokensValidChange(allTokensFilled);
        }
    }, [allTokensFilled, onTokensValidChange]);

    // Handle token value change
    const handleTokenChange = (key: string, value: string) => {
        setPromptParts((prevParts: PromptPart[]) => 
            prevParts.map((part: PromptPart) => 
                part.type === 'token' && part.key === key 
                    ? { ...part, value }
                    : part
            )
        );
    };

    let allSchoolDistrictsOptionLabels = schoolDistrictOptionLabels;
    const [allTags, setAllTags] = useState<OptionType[]>([]);

    useEffect(() => {
        if (listing) {
            const images = listing.images;
            const uniqueTags = Array.from(
                new Set(images?.map((image) => image.tag))
            )
                .sort((a, b) => a.localeCompare(b))
                .map((tag) => ({
                    label: tag,
                    value: tag,
                }));
            setAllTags(uniqueTags);
        }
    }, [listing]);

    const selectedDistrictOption = useMemo(() => {
        if (!schoolDistrict) return null;

        // exact match first
        const direct = allSchoolDistrictsOptionLabels.find(
            (opt) => opt.value === schoolDistrict
        );
        if (direct) return direct;

        // otherwise find best fuzzy match
        let best: OptionType | null = null;
        let bestScore = 0;
        const threshold = 0.6; // require at least 60% similarity

        for (const opt of allSchoolDistrictsOptionLabels) {
            const score = similarity(schoolDistrict, opt.value);
            if (score > bestScore) {
                bestScore = score;
                best = opt;
            }
        }
        return bestScore >= threshold ? best : null;
    }, [schoolDistrict, allSchoolDistrictsOptionLabels]);

    useEffect(() => {
        if (selectedDistrictOption && setSchoolDistrict) {
            setSchoolDistrict(selectedDistrictOption.value);
        }
    }, [selectedDistrictOption, setSchoolDistrict]);

    // Debug: Log when leadGenerationPrompts changes
    useEffect(() => {
        console.log("leadGenerationPrompts state changed:", leadGenerationPrompts);
    }, [leadGenerationPrompts]);

    useEffect(() => {
        const fetchListingList = async () => {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            try {
                const response = await axios.get(
                    `${backendUrl}/post/listing-list`,
                    {
                        headers,
                    }
                );

                if (response.status !== 200) {
                    throw new Error(
                        "Failed to fetch listings and promotion list."
                    );
                }


                       // Sort: regular property listings (MLSListing, RentcastListing, etc.) first,
                                // special types (My Brand, Lead Generation, Text Only) last — preserving
                                // relative order within each group.
                                const norm = (str: string) => (str || '').toLowerCase().replace(/\s/g, '');
                                const SPECIAL_TYPES = ['mybrand', 'leadgeneration', 'textonly'];
                                const isSpecial = (item: { listingType: string }) => SPECIAL_TYPES.includes(norm(item.listingType));
                                const sortedArr = [...response.data].sort((a, b) => {
                                        const aSpecial = isSpecial(a);
                                        const bSpecial = isSpecial(b);
                                        if (!aSpecial && bSpecial) return -1;
                                        if (aSpecial && !bSpecial) return 1;
                                        return 0;
                                });
                                // Clone the MyBrand entry as "Text Only Post"
                                const myBrandEntry = sortedArr.find((item) => norm(item.listingType) === 'mybrand');
                                const textOnlyEntry = myBrandEntry
                                    ? { ...myBrandEntry, listingType: ListingType.TextOnly, address: 'Text Only Post', displayName: 'Text Only Post' }
                                    : null;
                                setListings?.(textOnlyEntry ? [...sortedArr, textOnlyEntry] : sortedArr);
            } catch (error: unknown) {
                console.error("Error fetching listings: ", error);
                toast.error(
                    <CustomToast
                        message="Failed to fetch listings and promotion list. Please try again later."
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
            }
        };

        const fetchData = async () => {
            if (isAdHocPost) {
                setLoading(true);
                await Promise.all([fetchListingList()]);
                setLoading(false);
            }
        };

        fetchData();
    }, [isAdHocPost, setListings]);

    const fetchLeadGenerationSubtypes = async () => {
        setLoadingSubtypes(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.get(
                `${backendUrl}/lead-generation/subtypes`,
                { headers }
            );
            
            if (response.status === 200) {
                setLeadGenerationSubtypes(response.data.subtypes || []);
            }
        } catch (error) {
            console.error("Error fetching lead generation subtypes:", error);
            toast.error(
                <CustomToast
                    message="Failed to fetch lead generation types. Please try again later."
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setLoadingSubtypes(false);
        }
    };

    const normalizePromptBrackets = (str: string): string => {
      if (typeof str !== "string") return "";

      let normalized = str.trim();

      // Replace nested/multiple curly brackets with single square brackets
      normalized = normalized.replace(/({+\s*)([^{}]+?)(\s*}+)/g, "[$2]");

      // Replace remaining single curly brackets
      normalized = normalized.replace(/{([^{}]+)}/g, "[$1]");

      // Remove any stray curly brackets
      normalized = normalized.replace(/[{}]/g, "");

      return normalized;
    };

  const containsValidPlaceholder = (str: string): boolean => {
    return typeof str === "string" && /\[[^\]]+\]/.test(str);
  };

    const fetchLeadGenerationPrompts = async (
      subtype: string,
      // Passed explicitly by the audience switch so it never reads a stale
      // value from the closure it was created in.
      audience: ContentAudience = contentAudience,
    ) => {
      const cacheKey = `${audience}|${subtype}`;

      // Check if prompts are already cached for this subtype and audience
      if (promptsCache[cacheKey]) {
        setLeadGenerationPrompts(promptsCache[cacheKey]);
        setSelectedPrompt(""); // Reset selected prompt
        setNoValidPrompts(promptsCache[cacheKey].length === 0);
        return;
      }

      setLoadingPrompts(true);
      setSelectedPrompt(""); // Reset selected prompt
      try {
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await axios.post(
          `${backendUrl}/lead-generation/generate-options`,
          { subtype, audience },
          { headers },
        );

        if (
          (response.status === 200 || response.status === 201) &&
          Array.isArray(response.data.options)
        ) {
          // Normalize and filter prompts
          const filteredPrompts = response.data.options
            .filter((opt: string) => typeof opt === "string")
            .map((opt: string) => normalizePromptBrackets(opt))
            .filter((opt: string) => containsValidPlaceholder(opt));

          if (filteredPrompts.length > 0) {
            setLeadGenerationPrompts(filteredPrompts);
            setPromptsCache((prev) => ({
              ...prev,
              [cacheKey]: filteredPrompts,
            }));
            setNoValidPrompts(false);
          } else {
            setLeadGenerationPrompts([]);
            setPromptsCache((prev) => ({ ...prev, [cacheKey]: [] }));
            setNoValidPrompts(true);
          }
        } else {
          setLeadGenerationPrompts([]);
          setPromptsCache((prev) => ({ ...prev, [cacheKey]: [] }));
          setNoValidPrompts(true);
        }
      } catch (error) {
        console.error("Error fetching lead generation prompts:", error);
        toast.error(
          <CustomToast
            message="Failed to fetch prompts. Please try again later."
            type="error"
          />,
          { autoClose: 3000 },
        );
      } finally {
        setLoadingPrompts(false);
      }
    };

    const handleLeadGenerationTypeChange = (value: string) => {
        setLeadGenerationType(value);
        setGeneralInstructions("");
        if (value) {
            fetchLeadGenerationPrompts(value);
        } else {
            setLeadGenerationPrompts([]);
            setSelectedPrompt("");
        }
    };

    // Switching voice mid-wizard invalidates anything already drafted
    // from the other voice, so the lead-gen selections are cleared and the
    // options refetched for the new audience.
    const handleContentAudienceChange = (value: ContentAudience) => {
        if (value === contentAudience) return;

        setContentAudience(value);

        if (isLeadGenListing && leadGenerationType) {
            setGeneralInstructions("");
            setSelectedPrompt("");
            setPromptParts([]);
            fetchLeadGenerationPrompts(leadGenerationType, value);
        }
    };

    // When the user checks "Post Captioned Video", drop any selections that the
    // captioned-video flow doesn't support so the UI doesn't show stale state.
    // Captioned-video only allows MLS / Rentcast / MyBrand / My Niche Video
    // listings (see CAPTIONED_VIDEO_ALLOWED_LISTING_TYPES in adhocListingTypes)
    // — anything else (Lead Generation, Text Only, Promotion) gets cleared
    // along with its dependent lead-gen dropdown selections; unticking drops
    // My Niche Video, which only exists inside the captioned flow.
    const handlePostCaptionedVideoToggle = () => {
        const next = !postCaptionedVideo;
        setPostCaptionedVideo(next);
        if (selectedListing && !isListingShownInPicker(selectedListing.listingType, next)) {
            setSelectedListing?.(undefined);
        }
        if (!next) {
            // MB-1461: the review opt-in only exists inside the
            // captioned-video flow — clear it when the flow is deselected.
            setReviewCaptions?.(false);
            return;
        }

        // Ticking captioned turns AI Enhance on once; Back to this step must not undo the user's untick.
        setAiEnhance(true);
        setLeadGenerationType("");
        setLeadGenerationSubtypes([]);
        setLeadGenerationPrompts([]);
        setSelectedPrompt("");
        setPromptParts([]);
    };

    const handleSelectListing = (compositeKey: string) => {
        if (!listings) return;

        const [campaignId, listingType] = compositeKey.split('|||');
        const selected = pickerListings.find((l) => l.campaignId === campaignId && l.listingType === listingType);

        if (selected) {
            setSelectedListing?.(selected);
            // Reset lead generation type when changing listings
            if (selected.listingType.toLowerCase().replace(/\s/g, '') !== "leadgeneration") {
                setLeadGenerationType("");
                setLeadGenerationSubtypes([]);
                setLeadGenerationPrompts([]);
                setSelectedPrompt("");
            } else {
                // setAreTokensFilled?.(false);
                // Fetch subtypes when lead generation is selected
                fetchLeadGenerationSubtypes();
            }
        }

        const normalizedType = selected?.listingType.toLowerCase().replace(/\s/g, '');
    if (normalizedType === "leadgeneration" || normalizedType === "textonly") {
        confirmAiEnhanceDisable();
    } else if (normalizedType === "mybrand") {
        if (postCaptionedVideo) {
            setAiEnhance?.(true);
        } else {
            confirmAiEnhanceDisable();
        }
    } else {
        setAiEnhance?.(true);
    }
    };
    

    // The card's pick goes through the manual-pick handler, and only while nothing is picked: Back remounts
    // this step, so a once-per-mount pick would overwrite the user's choice. The ref keeps the handler,
    // re-created every render, out of the effect's dependencies.
    const handleSelectListingRef = useRef(handleSelectListing);
    handleSelectListingRef.current = handleSelectListing;
    useEffect(() => {
        if (!preset || selectedListing || !listings || listings.length === 0) return;
        const pick = presetPick(listings, preset, nicheEligible);
        if (pick) handleSelectListingRef.current(listingSelectValue(pick));
    }, [listings, preset, nicheEligible, selectedListing]);

    return (
      <div className="relative h-full space-y-6">
        {(loading || loadingSubtypes || loadingPrompts) && <LoadingScreen />}
        {/* Open House Date and Time Fields */}
        {isOpenHousePost &&
          setOpenHouseDate &&
          setStartTime &&
          setEndTime &&
          setStartPeriod &&
          setEndPeriod &&
          openHouseDate &&
          startTime &&
          endTime && (
            <div className="grid gap-4 md:grid-cols-2">
              {/* Date Field */}
              <div>
                <Label className="text-primary font-semibold">
                  Open House Date
                </Label>
                <div className="mt-2">
                  <Popover
                    open={isCalendarOpen}
                    onOpenChange={setIsCalendarOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "justify-start text-left font-normal",
                          !openHouseDate && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {openHouseDate
                          ? format(openHouseDate, "PPP")
                          : "Change date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CustomDatePicker
                        date={openHouseDate}
                        setDate={setOpenHouseDate}
                        restriction="none"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              {/* Time Fields */}
              <div>
                <Label className="text-primary font-semibold">
                  Open House Time
                </Label>
                <div
                  className={`mt-2 grid ${
                    isMobile ? "grid-cols-1" : "grid-cols-2"
                  } gap-4`}
                >
                  {/* Start Time */}
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <Select
                        value={startTime.hour.toString()}
                        onValueChange={(value) =>
                          setStartTime({
                            ...startTime,
                            hour: Number.parseInt(value),
                          })
                        }
                      >
                        <SelectTrigger className="w-[70px]">
                          <SelectValue placeholder="Hour" />
                        </SelectTrigger>
                        <SelectContent>
                          {hourOptions.map((hour) => (
                            <SelectItem
                              key={`start-hour-${hour}`}
                              value={hour.toString()}
                            >
                              {hour.toString().padStart(2, "0")}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={startTime.minute.toString()}
                        onValueChange={(value) =>
                          setStartTime({
                            ...startTime,
                            minute: Number.parseInt(value),
                          })
                        }
                      >
                        <SelectTrigger className="w-[70px]">
                          <SelectValue placeholder="Min" />
                        </SelectTrigger>
                        <SelectContent>
                          {minuteOptions.map((minute) => (
                            <SelectItem
                              key={`start-min-${minute}`}
                              value={minute.toString()}
                            >
                              {minute.toString().padStart(2, "0")}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={startPeriod}
                        onValueChange={setStartPeriod}
                      >
                        <SelectTrigger className="w-[70px]">
                          <SelectValue placeholder="AM/PM" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AM">AM</SelectItem>
                          <SelectItem value="PM">PM</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {/* End Time */}
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <Select
                        value={endTime.hour.toString()}
                        onValueChange={(value) =>
                          setEndTime({
                            ...endTime,
                            hour: Number.parseInt(value),
                          })
                        }
                      >
                        <SelectTrigger className="w-[70px]">
                          <SelectValue placeholder="Hour" />
                        </SelectTrigger>
                        <SelectContent>
                          {hourOptions.map((hour) => (
                            <SelectItem
                              key={`end-hour-${hour}`}
                              value={hour.toString()}
                            >
                              {hour.toString().padStart(2, "0")}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={endTime.minute.toString()}
                        onValueChange={(value) =>
                          setEndTime({
                            ...endTime,
                            minute: Number.parseInt(value),
                          })
                        }
                      >
                        <SelectTrigger className="w-[70px]">
                          <SelectValue placeholder="Min" />
                        </SelectTrigger>
                        <SelectContent>
                          {minuteOptions.map((minute) => (
                            <SelectItem
                              key={`end-min-${minute}`}
                              value={minute.toString()}
                            >
                              {minute.toString().padStart(2, "0")}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={endPeriod} onValueChange={setEndPeriod}>
                        <SelectTrigger className="w-[70px]">
                          <SelectValue placeholder="AM/PM" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AM">AM</SelectItem>
                          <SelectItem value="PM">PM</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        {/* VideoPostType Selection */}
        {isVideoPost && setVideoPostType && (
          <div className="flex flex-col justify-center items-start space-y-2">
            <Label className="text-primary font-semibold">
              Select Video Showcase Post Type
            </Label>
            <Select
              value={videoPostType || ""}
              onValueChange={(e) => setVideoPostType(e as VideoPostType)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Video Showcase Post Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Walkthrough">Walkthrough</SelectItem>
                <SelectItem value="SchoolDistrict">School District</SelectItem>
                <SelectItem value="Feature">Feature</SelectItem>
                <SelectItem value="Neighborhood">Neighborhood</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {isVideoPost &&
          videoPostType === VideoPostType.SchoolDistrict &&
          setSchoolDistrict && (
            <div>
              <Label className="text-primary font-semibold">
                Select School District
              </Label>
              <CustomSelect
                id="#select-school-district-dropdown"
                options={allSchoolDistrictsOptionLabels}
                value={selectedDistrictOption}
                onChange={(opt) => setSchoolDistrict(opt ? opt.value : "")}
                placeholder="Select School District"
                isClearable={false}
                className="w-full mt-1"
                bg="#FFFFFF"
                isCreatable={true}
                onCreateOption={(inputValue: string) => {
                  const titleCased = inputValue
                    .split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ");
                  allSchoolDistrictsOptionLabels.push({
                    label: titleCased,
                    value: inputValue,
                  });
                  setSchoolDistrict(inputValue);
                }}
              />
            </div>
          )}

        {isVideoPost && videoPostType === VideoPostType.Feature && setTags && (
          <div>
            <Label className="text-primary font-semibold">Select Tags</Label>
            <CustomSelect
              id="#select-school-district-dropdown"
              isMulti={true}
              maxSelectedOptions={4}
              options={allTags}
              value={tags?.map((tag) => ({
                label: tag,
                value: tag,
              }))}
              onChange={(selectedOptions: readonly OptionType[]) => {
                setTags(selectedOptions.map((option) => option.value));
              }}
              placeholder="Select Tags"
              isClearable={false}
              className="w-full mt-1"
              bg="#FFFFFF"
              isCreatable={false}
            />
          </div>
        )}

        {isAdHocPost && setSelectedListing && !preset && (
          <div className="flex items-start gap-2 mt-4">
            <div className="flex-none mt-0.5">
              <Checkbox
                id="post-captioned-video"
                small={true}
                checked={postCaptionedVideo}
                onChange={handlePostCaptionedVideoToggle}
              />
            </div>
            <div className="flex flex-col">
              <label
                htmlFor="post-captioned-video"
                className="text-sm font-semibold text-primary cursor-pointer leading-5"
              >
                Post Captioned Video
              </label>
              <span className="text-sm text-gray-500 leading-4 mt-0.5">
                Automatically adds captions to your video.
              </span>
            </div>
          </div>
        )}

        {/* MB-1461: opt-in pre-render transcript review — only meaningful
            when the captioned-video flow is selected. */}
        {isAdHocPost && setSelectedListing && postCaptionedVideo && setReviewCaptions && (
          <div className="flex items-start gap-2 mt-2">
            <div className="flex-none mt-0.5">
              <Checkbox
                id="review-captions-before-rendering"
                small={true}
                checked={!!reviewCaptions}
                onChange={() => setReviewCaptions(!reviewCaptions)}
              />
            </div>
            <div className="flex flex-col">
              <label
                htmlFor="review-captions-before-rendering"
                className="text-sm font-semibold text-primary cursor-pointer leading-5"
              >
                Review captions before rendering
              </label>
              <span className="text-sm text-gray-500 leading-4 mt-0.5">
                Check the transcript for accuracy before your video is
                rendered. You&apos;ll stay in this window while we prepare it
                (usually 1-2 minutes).
              </span>
            </div>
          </div>
        )}

        {isAdHocPost && preset?.card === "my-brand" && myBrandRow && (
          <div className="flex items-start gap-2 mt-4">
            <div className="flex-none mt-0.5">
              <Checkbox
                id="text-only-post"
                small={true}
                checked={isTextOnlySelected}
                onChange={() => {
                  const target = isTextOnlySelected ? myBrandRow : textOnlyRow;
                  if (target) handleSelectListing(listingSelectValue(target));
                }}
              />
            </div>
            <div className="flex flex-col">
              <label
                htmlFor="text-only-post"
                className="text-sm font-semibold text-primary cursor-pointer leading-5"
              >
                Text only post
              </label>
              <span className="text-sm text-gray-500 leading-4 mt-0.5">
                No photos or video. Facebook, X and LinkedIn.
              </span>
            </div>
          </div>
        )}

        {isAdHocPost && presetPickMissing && (
          <p className="text-sm text-gray-600">{presetMissingText}</p>
        )}

        {isAdHocPost && setSelectedListing && (!preset || presetRows.length > 0) && (
          <div>
            <Label className="text-primary font-semibold">
              Choose an Active Campaign
            </Label>
            <Select
              onValueChange={(value) => handleSelectListing(value as string)}
              value={listingSelectValue(selectedListing)}
            >
              <SelectTrigger id="select-listing-promotion">
                <SelectValue placeholder="Select a listing/campaign" />
              </SelectTrigger>
              <SelectContent id="post-type-select-content">
                {(preset
                  ? presetRows
                  : pickerListings.filter((listing) => isListingShownInPicker(listing.listingType, postCaptionedVideo)))
                  .map((listing) => (
                  <SelectItem
                    key={`${listing.campaignId}|||${listing.listingType}`}
                    value={`${listing.campaignId}|||${listing.listingType}`}
                  >
                    {listing.displayName || listing.address || "Promotion"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Audience — per piece, not per account. Lead Generation, MyBrand
            and My Niche Video only; every other flow stays residential. */}
        {isAdHocPost && (isLeadGenListing || isMyBrandListing || isNicheVideoSelected) && (
          <div>
            <Label className="text-primary font-semibold">Audience</Label>
            <RadioGroup
              value={contentAudience}
              onValueChange={(value) =>
                handleContentAudienceChange(value as ContentAudience)
              }
              className="flex flex-col gap-2 mt-1 min-[480px]:flex-row min-[480px]:gap-6"
            >
              {CONTENT_AUDIENCES.map((audience) => (
                <Label
                  key={audience}
                  htmlFor={`content-audience-${audience.toLowerCase()}`}
                  className="flex items-center space-x-2 p-2 text-primary cursor-pointer"
                >
                  <RadioGroupItem
                    value={audience}
                    id={`content-audience-${audience.toLowerCase()}`}
                    // Match the Post Captioned Video checkbox above: same 22px
                    // box, same gold border on white. Overridden here rather
                    // than on the shared primitive, which two other screens use.
                    className="h-[22px] w-[22px] border-secondary bg-white [&>span>div]:h-2.5 [&>span>div]:w-2.5"
                  />
                  <span>{audience}</span>
                </Label>
              ))}
            </RadioGroup>
            <p className="text-xs text-muted-foreground mt-1">
              {contentAudienceHint(contentAudience, isMyBrandListing || isNicheVideoSelected)}
            </p>
          </div>
        )}

        {/* Lead Generation Type Dropdown */}
        {isAdHocPost &&
          selectedListing?.listingType.toLowerCase().replace(/\s/g, "") ===
            "leadgeneration" && (
            <div>
              <Label className="text-primary font-semibold">
                Lead Generation Type
              </Label>
              <Select
                onValueChange={(value) => handleLeadGenerationTypeChange(value)}
                value={leadGenerationType}
                disabled={loadingSubtypes}
              >
                <SelectTrigger id="select-lead-generation-type">
                  <SelectValue
                    placeholder={
                      loadingSubtypes
                        ? "Loading types..."
                        : "Select lead generation type"
                    }
                  />
                </SelectTrigger>
                <SelectContent id="lead-generation-type-select-content">
                  {leadGenerationSubtypes.map((subtype) => (
                    // Value stays the api's subtype; only the label
                    // follows the audience.
                    <SelectItem key={subtype} value={subtype}>
                      {leadGenSubtypeLabel(subtype, contentAudience)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

        {/* Lead Generation Prompts Dropdown */}
{isAdHocPost && leadGenerationType && (
  <div>
    <Label className="text-primary font-semibold">Select Prompt</Label>
    <Select
      onValueChange={(value) => setSelectedPrompt(value)}
      value={selectedPrompt}
      disabled={loadingPrompts}
    >
      <SelectTrigger
        id="select-lead-generation-prompt"
        className={isMobile ? "max-w-full min-w-0" : "min-w-0"}
        style={isMobile ? { minWidth: 0, width: "100%" } : {}}
      >
        <SelectValue
          placeholder={
            loadingPrompts ? "Loading prompts..." : "Select a prompt"
          }
        />
      </SelectTrigger>
      <SelectContent
        id="lead-generation-prompt-select-content"
        className={isMobile ? "max-h-[40vh]" : ""}
        style={{ width: "var(--radix-select-trigger-width)", maxWidth: "var(--radix-select-trigger-width)", minWidth: 0 }}
        position="popper"
        side={isMobile ? "top" : undefined}
        avoidCollisions={true}
      >
        {noValidPrompts ? (
          <SelectItem value="no-prompts" disabled>
            No prompts found
          </SelectItem>
        ) : (
          leadGenerationPrompts.map((prompt, index) => (
            <SelectItem
              key={index}
              value={prompt}
              className="whitespace-pre-line break-words px-2 py-2"
              style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}
              title={prompt}
            >
              {prompt}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  </div>
)}

        {/* Inline Token Editor for Lead Generation */}
        {isAdHocPost && selectedPrompt && promptParts.length > 0 && (
          <div className="space-y-3">
            <Label className="text-primary font-semibold">Post Content</Label>
            <div className="p-4 border rounded-md min-h-[120px]">
              <div className="text-base leading-relaxed">
                {promptParts.map((part, index) => {
                  if (part.type === "text") {
                    return (
                      <span
                        key={`text-${index}`}
                        className="text-gray-700 whitespace-pre-wrap"
                      >
                        {part.value}
                      </span>
                    );
                  } else {
                    const isFilled = part.value && part.value.trim().length > 0;
                    const displayLength = Math.max(
                      part.value?.length || 0,
                      part.key?.length || 0,
                      5,
                    );
                    const inputWidth = Math.max(displayLength + 3, 8);

                    // Calculate new value length if this token changes
                    return (
                      <input
                        key={`token-${part.key}-${index}`}
                        ref={(el) => {
                          if (part.key) {
                            tokenInputRefs.current[part.key] = el;
                          }
                        }}
                        type="text"
                        value={part.value}
                        maxLength={CHARACTER_LIMIT}
                        onChange={(e) => {
                          // Simulate new prompt with this token changed
                          const newParts = promptParts.map((p, i) =>
                            i === index && part.type === "token"
                              ? { ...p, value: e.target.value }
                              : p,
                          );
                          const newLength =
                            buildPromptFromParts(newParts).length;
                          if (newLength <= CHARACTER_LIMIT) {
                            part.key &&
                              handleTokenChange(part.key, e.target.value);
                          }
                        }}
                        placeholder={part.key}
                        className={`inline-block px-3 py-1 rounded-md text-base font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 mx-0.5 my-1 ${
                          isFilled
                            ? "bg-[#45bf55]/10 border-2 border-[#45bf55] text-[#4b5563] focus:ring-[#45bf55]"
                            : "bg-[#FA9600]/10 border-2 border-dashed border-[#FA9600] text-[#FA9600] focus:ring-[#FA9600]"
                        }`}
                        style={{
                          width: `${inputWidth}ch`,
                          minWidth: "8ch",
                          maxWidth: "30ch",
                          verticalAlign: "middle",
                        }}
                      />
                    );
                  }
                })}
              </div>
            </div>
            <p
              className={`text-sm text-muted-foreground mt-1 text-right w-full ${
                promptCharCount > CHARACTER_LIMIT ? "text-red-500" : ""
              }`}
            >
              {promptCharCount} / {CHARACTER_LIMIT} characters
            </p>
            {!allTokensFilled && (
              <div className="bg-gray-100 p-3 rounded-md mt-4">
                <p className="text-sm text-gray-600">
                  <strong className="text-primary">Tip:</strong> Fill in the
                  highlighted fields above to personalize your post.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Post Content / AI Instructions */}
        {!hideContent && !postCaptionedVideo && (!isAdHocPost || !selectedPrompt || promptParts.length === 0) && (
          <div className="space-y-6 mt-6">
            <div className="flex flex-col justify-center items-start space-y-2">
              <Label className="text-primary font-semibold">
                {selectedListing?.listingType === "MLSListing" ||
                selectedListing?.listingType === "RentcastListing"
                  ? `Post Content / AI Instructions`
                  : "Post Content"}
              </Label>
              <Textarea
                className="w-full p-2 border rounded-md min-h-[150px]"
                placeholder={
                  aiEnhance
                    ? "Provide a few lines of text or bullet points so Marabot can generate the post content for you."
                    : "Please enter the post content."
                }
                value={generalInstructions}
                onChange={(e) => setGeneralInstructions(e.target.value)}
                maxLength={
                  selectedListing?.listingType
                    .toLowerCase()
                    .replace(/\s/g, "") === "leadgeneration"
                    ? 200
                    : aiEnhance
                      ? 500
                      : 4200
                }
              />
              <p
                className={`text-sm text-muted-foreground mt-1 text-right w-full ${
                  selectedListing?.listingType
                    .toLowerCase()
                    .replace(/\s/g, "") === "leadgeneration"
                    ? generalInstructions.length > 200
                      ? "text-red-500"
                      : ""
                    : aiEnhance
                      ? generalInstructions.length > 500
                        ? "text-red-500"
                        : ""
                      : generalInstructions.length > 4200
                        ? "text-red-500"
                        : ""
                }`}
              >
                {selectedListing?.listingType
                  .toLowerCase()
                  .replace(/\s/g, "") === "leadgeneration"
                  ? `${generalInstructions.length} / 200 characters`
                  : aiEnhance
                    ? `${generalInstructions.length} / 500 characters`
                    : `${generalInstructions.length} / 4200 characters`}
              </p>
            </div>
          </div>
        )}

        {/* AI Enhance checkbox — see showsAiEnhanceToggle */}
        {!hideAiEnhance &&
          selectedListing?.listingType &&
          showsAiEnhanceToggle(selectedListing.listingType, postCaptionedVideo) &&
          (!isAdHocPost || !selectedPrompt || promptParts.length === 0) && (
            <div className="flex items-start gap-2 mt-4">
              <div className="flex-none mt-0.5">
                <Checkbox
                  id="ai-enhance"
                  small={true}
                  checked={aiEnhance}
                  onChange={() => setAiEnhance(!aiEnhance)}
                />
              </div>
              <div className="flex flex-col">
                <label
                  htmlFor="ai-enhance"
                  className="text-sm font-semibold text-primary cursor-pointer leading-5"
                >
                  AI Enhance
                </label>
                <span className="text-sm text-gray-500 leading-4 mt-0.5">
                  {postCaptionedVideo
                    ? "AI will enhance or generate the post based on transcript extracted from the video."
                    : "AI will enhance or generate the post based on your instructions."}
                </span>
              </div>
            </div>
          )}
      </div>
    );
};

export default BasicInfoStep;
